import { useSyncExternalStore } from 'react';

// Client-side mirror of a clip's server render state (models/prompt.go
// RenderStatus/RenderProgress/RenderError) plus the live "RenderProgress" WS
// stream. The list refetch that fires on completion doesn't fire per progress
// tick, so the bar is driven from this store rather than from clip props.

export type ClipRenderStatus = 'idle' | 'rendering' | 'ready' | 'failed';

export interface ClipRenderState {
  status: ClipRenderStatus;
  /** 0..1 */
  progress: number;
  /** free-form backend stage: rendering | encoding | muxing | stills | ... */
  stage: string;
  error: string;
  /** A generated-style render the user may cancel (set after its POST, or by a package-only stage). */
  cancellable?: boolean;
}

export const IDLE_RENDER_STATE: ClipRenderState = {
  status: 'idle',
  progress: 0,
  stage: '',
  error: '',
};

/** Render fields as served on a clip object (JSON omitempty). */
export interface ClipRenderFields {
  render_status?: string;
  render_progress?: number;
  render_error?: string;
}

/** Payload of the backend "RenderProgress" WS event. */
export interface RenderProgressEvent {
  clip_id?: string;
  status?: string;
  progress?: number;
  stage?: string;
  error?: string;
}

const normalizeStatus = (raw: string | undefined): ClipRenderStatus => {
  switch (raw) {
    case 'rendering':
      return 'rendering';
    case 'ready':
      return 'ready';
    case 'failed':
      return 'failed';
    default:
      return 'idle';
  }
};

const clampProgress = (value: number | undefined): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
};

// ==================== Pure reducers (unit-tested) ====================

export const renderStateFromClip = (fields: ClipRenderFields): ClipRenderState => {
  const status = normalizeStatus(fields.render_status);
  return {
    status,
    progress: clampProgress(fields.render_progress),
    stage: '',
    error: status === 'failed' ? fields.render_error || '' : '',
  };
};

/**
 * True for a render the user cancelled rather than one that crashed. The
 * backend reports a completed cancel exactly like any other failure — same
 * terminal status, same "failed" stage — and this literal error text is the
 * only thing that tells the two apart (contentgen-go-backend
 * internal/api/clip/packageRender.go:525, `failState("Cancelled.")`; it's a
 * literal string there, not a named constant, so this text has to be kept in
 * sync by hand if that ever changes).
 */
export const isCancelledRender = (state: ClipRenderState): boolean =>
  state.status === 'failed' && state.error === 'Cancelled.';

// Stages only a generated-style render reports while the bridge still runs it.
const CANCELLABLE_STAGES = new Set(['sealing']);

export const applyRenderProgressEvent = (
  prev: ClipRenderState,
  event: RenderProgressEvent
): ClipRenderState => {
  const status = normalizeStatus(event.status);
  const stage = event.stage || '';
  const next: ClipRenderState = {
    status,
    progress: clampProgress(event.progress),
    stage,
    error: status === 'failed' ? event.error || prev.error || '' : '',
  };
  if (status === 'rendering' && stage !== 'uploading' && (prev.cancellable || CANCELLABLE_STAGES.has(stage))) {
    next.cancellable = true;
  }
  return next;
};

/**
 * Reconcile a fresh clip snapshot against whatever the store already holds.
 * A live render (WS "rendering" events don't refetch the list, and the
 * optimistic bridge fires before the backend flips render_status) or a
 * completed/failed render must survive a non-terminal snapshot; only a terminal
 * snapshot (ready/failed) may supersede an active/terminal store entry.
 *
 * Forward-progress exception: when both prev and seed are "rendering", a seed
 * with strictly HIGHER progress is accepted. That lets a refetch-driven GET
 * /clips (which carries fresher server-side render_progress) advance the bar
 * even when the live WS progress stream is being dropped in transport. A lower
 * or equal rendering seed is still rejected — that's the stale-snapshot guard.
 */
export const reconcileSeed = (
  prev: ClipRenderState | undefined,
  fields: ClipRenderFields
): ClipRenderState => {
  const seeded = renderStateFromClip(fields);
  if (!prev) return seeded;
  const seedIsTerminal = seeded.status === 'ready' || seeded.status === 'failed';
  if (seedIsTerminal) return seeded;
  const prevIsActiveOrTerminal = prev.status !== 'idle';
  if (prevIsActiveOrTerminal) {
    if (
      prev.status === 'rendering' &&
      seeded.status === 'rendering' &&
      seeded.progress > prev.progress
    ) {
      // Keep the live WS stage label; only the progress moved forward.
      const advanced: ClipRenderState = { ...seeded, stage: prev.stage };
      if (prev.cancellable) advanced.cancellable = true;
      return advanced;
    }
    return prev;
  }
  return seeded;
};

// ==================== Module-level store ====================

const store = new Map<string, ClipRenderState>();
const listeners = new Map<string, Set<() => void>>();
// Store-wide listeners (not keyed by clip) used by the polling fallback to know
// when at least one tracked clip is rendering.
const globalListeners = new Set<() => void>();

// Timestamp (ms) of the last genuine WS "RenderProgress" event. The polling
// fallback uses this to skip ticks while live delivery is clearly working.
let lastRenderEventAt = 0;

const emit = (clipId: string) => {
  listeners.get(clipId)?.forEach((fn) => fn());
  globalListeners.forEach((fn) => fn());
};

const setState = (clipId: string, next: ClipRenderState) => {
  store.set(clipId, next);
  emit(clipId);
};

export const getClipRenderState = (clipId: string): ClipRenderState =>
  store.get(clipId) ?? IDLE_RENDER_STATE;

/** Seed/reconcile a clip's render state from its served fields. */
export const seedClipRenderState = (clipId: string, fields: ClipRenderFields): void => {
  if (!clipId) return;
  const next = reconcileSeed(store.get(clipId), fields);
  if (next !== store.get(clipId)) setState(clipId, next);
};

/** Apply a live "RenderProgress" WS event. */
export const handleRenderProgressEvent = (event: RenderProgressEvent): void => {
  const clipId = event.clip_id;
  if (!clipId) return;
  lastRenderEventAt = Date.now();
  setState(clipId, applyRenderProgressEvent(getClipRenderState(clipId), event));
};

/** True when at least one tracked clip is currently rendering. */
export const anyClipRendering = (): boolean => {
  let rendering = false;
  store.forEach((state) => {
    if (state.status === 'rendering') rendering = true;
  });
  return rendering;
};

/** ms timestamp of the last genuine WS RenderProgress event (0 if none yet). */
export const getLastRenderEventAt = (): number => lastRenderEventAt;

/** Subscribe to any store change (used by the polling fallback selector). */
export const subscribeRenderStore = (listener: () => void): (() => void) => {
  globalListeners.add(listener);
  return () => {
    globalListeners.delete(listener);
  };
};

/** Optimistic bridge: flip to rendering the instant a render POST is fired. */
export const markClipRenderStarting = (clipId: string): void => {
  if (!clipId) return;
  setState(clipId, { status: 'rendering', progress: 0, stage: 'starting', error: '' });
};

/** A generated-style render was started: offer Cancel while it runs. */
export const markClipRenderCancellable = (clipId: string): void => {
  const prev = store.get(clipId);
  if (!clipId || !prev || prev.status !== 'rendering') return;
  setState(clipId, { ...prev, cancellable: true });
};

/**
 * Completion signal (UpdateClipPromptFileURL): clear a live render. A no-op
 * unless a render is in flight, so a stray completion echo can't disturb an
 * idle/failed card.
 */
export const markClipRenderComplete = (clipId: string): void => {
  if (!clipId) return;
  const prev = store.get(clipId);
  if (prev && prev.status !== 'rendering') return;
  setState(clipId, { status: 'ready', progress: 1, stage: '', error: '' });
};

/** Force a clip back to idle (e.g. the render POST itself failed to start). */
export const clearClipRenderState = (clipId: string): void => {
  if (!clipId) return;
  setState(clipId, IDLE_RENDER_STATE);
};

/**
 * Puts back a snapshot captured before an optimistic update — used when a
 * refusal that ran nothing (e.g. declining the confirm-render prompt) must
 * leave the clip's earlier render state exactly as it was, rather than
 * blanking it to idle. See `useClipActions`'s decline branch.
 */
export const restoreClipRenderState = (clipId: string, state: ClipRenderState): void => {
  if (!clipId) return;
  setState(clipId, state);
};

const subscribe = (clipId: string, listener: () => void): (() => void) => {
  let set = listeners.get(clipId);
  if (!set) {
    set = new Set();
    listeners.set(clipId, set);
  }
  set.add(listener);
  return () => {
    set!.delete(listener);
    if (set!.size === 0) listeners.delete(clipId);
  };
};

export const useClipRenderState = (clipId: string): ClipRenderState =>
  useSyncExternalStore(
    (listener) => subscribe(clipId, listener),
    () => getClipRenderState(clipId)
  );

/** Test-only reset. */
export const __resetRenderStateStore = (): void => {
  store.clear();
  listeners.clear();
  globalListeners.clear();
  lastRenderEventAt = 0;
};
